/**
 * Retailers workspace — /admin/retailers[/:nodeId | /unassigned]
 *
 * Left: the Retailer › Sub-retailer tree (and the Unassigned bucket of stores no
 * retailer owns yet). Right: the selected node's Locations and People, with bulk
 * actions, and a Settings button that opens the full retailer editor.
 *
 * Replaces the four flat tabs (Retailers · Retailer Locations · Retail Users ·
 * Invitations) so the hierarchy is visible and every assignment happens in place.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, Plus, Search, Settings, Loader2, AlertTriangle, MapPin, Users, Sparkles, X, Inbox } from 'lucide-react';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { RetailerTree, type TreeSelection } from '../../../components/admin/RetailerTree';
import { AdminRetailerEditModal } from '../AdminRetailerEditModal';
import { brandIconFor, deleteAdminRetailer } from '../../../services/adminRetailersApi';
import { fetchRetailerTree, type RetailerTreeNode, type RetailerTreeResponse } from '../../../services/adminRetailerTreeApi';
import { LocationsPane } from './LocationsPane';
import { PeoplePane } from './PeoplePane';
import { UnplacedPeople } from './UnplacedPeople';

type Tab = 'locations' | 'people';

export const RetailersWorkspace: React.FC = () => {
  const { isSuperAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const { nodeId } = useParams<{ nodeId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get('tab') === 'people' ? 'people' : 'locations';
  const setTab = (t: Tab) => setSearchParams(prev => { const n = new URLSearchParams(prev); if (t === 'locations') n.delete('tab'); else n.set('tab', t); return n; }, { replace: true });

  const [tree, setTree] = useState<RetailerTreeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [edit, setEdit] = useState<{ id: number | null; creating: boolean; parentId?: number; name?: string } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setTree(await fetchRetailerTree());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load retailers');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // ?new=1&name=… (from older links) opens the create form prefilled.
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setEdit({ id: null, creating: true, name: searchParams.get('name') ?? undefined });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const selection: TreeSelection = nodeId === 'unassigned' ? 'unassigned' : nodeId && /^\d+$/.test(nodeId) ? Number(nodeId) : null;
  const nodes = useMemo(() => tree?.nodes ?? [], [tree]);
  const node = typeof selection === 'number' ? nodes.find(n => n.id === selection) ?? null : null;
  const parent = node?.parentRetailerId ? nodes.find(n => n.id === node.parentRetailerId) ?? null : null;
  const children = node ? nodes.filter(n => n.parentRetailerId === node.id) : [];

  // Nothing selected yet → the first retailer with anything in it, else the first.
  useEffect(() => {
    if (!tree || selection !== null) return;
    const roots = nodes.filter(n => n.id !== 1 && n.parentRetailerId === null).sort((a, b) => a.name.localeCompare(b.name));
    const first = roots.find(n => n.counts.locations || n.counts.people || n.counts.waiting) ?? roots[0];
    if (first) navigate(`/admin/retailers/${first.id}`, { replace: true });
  }, [tree, selection, nodes, navigate]);

  const select = (sel: TreeSelection) => navigate(sel === 'unassigned' ? '/admin/retailers/unassigned' : sel === null ? '/admin/retailers' : `/admin/retailers/${sel}`);

  const counts = node ? (node.familyCounts ?? children.reduce((acc, c) => ({ locations: acc.locations + c.counts.locations, people: acc.people + c.counts.people, waiting: acc.waiting + c.counts.waiting }), { ...node.counts })) : null;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h4 className="fw-bold mb-0 d-flex align-items-center gap-2"><Building2 size={22} /> Retailers</h4>
          <p className="text-muted small mb-0">A retailer owns locations (stores on the trusscore.com map) and people. A sub-retailer is a retailer under another one. Pick a retailer to see and change what it owns.{!isSuperAdmin && <span className="ms-2 badge text-bg-secondary">Read-only</span>}</p>
        </div>
        {isSuperAdmin && (
          <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => setEdit({ id: null, creating: true })}><Plus size={14} /> New retailer</button>
        )}
      </div>

      {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}

      <div className="row g-3">
        {/* Tree */}
        <div className="col-12 col-lg-4 col-xl-3">
          <div className="bg-white rounded-3 shadow-sm">
            <div className="p-2 border-bottom">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><Search size={14} /></span>
                <input className="form-control" placeholder="Find a retailer…" value={filter} onChange={e => setFilter(e.target.value)} aria-label="Find a retailer" />
                {filter && <button type="button" className="btn btn-outline-secondary" onClick={() => setFilter('')}><X size={13} /></button>}
              </div>
            </div>
            {loading ? (
              <div className="p-3 text-muted small"><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
            ) : (
              <RetailerTree nodes={nodes} selected={selection} onSelect={select} filter={filter} unassigned={tree?.unassigned} />
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="col-12 col-lg-8 col-xl-9">
          {selection === 'unassigned' ? (
            <div className="bg-white rounded-3 shadow-sm p-3">
              <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2"><Inbox size={18} className="text-warning" /> Unassigned</h5>
              <p className="text-muted small mb-3">Stores nobody has placed under a retailer, and people the matcher could not place. Estimates shared with an unassigned store show quantities only.</p>
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item"><button type="button" className={`nav-link d-inline-flex align-items-center gap-1 ${tab === 'locations' ? 'active' : ''}`} onClick={() => setTab('locations')}><MapPin size={14} /> Locations <span className="badge text-bg-light border">{tree?.unassigned.locations ?? 0}</span></button></li>
                <li className="nav-item"><button type="button" className={`nav-link d-inline-flex align-items-center gap-1 ${tab === 'people' ? 'active' : ''}`} onClick={() => setTab('people')}><Users size={14} /> People{(tree?.unassigned.people ?? 0) > 0 ? <span className="badge text-bg-warning">{tree?.unassigned.people} waiting</span> : <span className="badge text-bg-light border">0</span>}</button></li>
              </ul>
              {tab === 'locations'
                ? <LocationsPane node={null} nodes={nodes} canEdit={isSuperAdmin} onChanged={() => void load()} />
                : <UnplacedPeople nodes={nodes} canEdit={isSuperAdmin} onChanged={() => void load()} />}
            </div>
          ) : node ? (
            <div className="bg-white rounded-3 shadow-sm p-3">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
                <div className="d-flex align-items-center gap-2">
                  {brandIconFor(node.id, node.brandIconUrl) && <img src={brandIconFor(node.id, node.brandIconUrl) ?? undefined} alt="" width={36} height={36} style={{ objectFit: 'contain', borderRadius: 6 }} />}
                  <div>
                    <h5 className="fw-bold mb-0 d-flex align-items-center gap-2 flex-wrap">
                      {node.name}
                      {!node.isActive && <span className="badge text-bg-secondary">paused</span>}
                      {node.isPartner && node.subdomain && <span className="badge text-bg-light border text-primary d-inline-flex align-items-center gap-1" title={`${node.subdomain}.estimator.trusscore.com`}><Sparkles size={12} /> {node.subdomain}</span>}
                    </h5>
                    <div className="text-muted small d-flex flex-wrap align-items-center gap-2">
                      {parent ? <span>Sub-retailer of <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => select(parent.id)}>{parent.name}</button>{node.pricingSource === 'parent' && <span className="badge text-bg-info ms-1">inherits pricing</span>}</span> : <span>Retailer</span>}
                      {children.length > 0 && <span>· {children.length} sub-retailer{children.length === 1 ? '' : 's'}</span>}
                      {counts && <span className="d-inline-flex align-items-center gap-1">· <MapPin size={12} /> {counts.locations} location{counts.locations === 1 ? '' : 's'} · <Users size={12} /> {counts.people} {counts.people === 1 ? 'person' : 'people'}{counts.waiting > 0 && <span className="badge text-bg-warning ms-1">{counts.waiting} waiting</span>}</span>}
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  {isSuperAdmin && !parent && node.id !== 1 && (
                    <button type="button" className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1" title={`Create a sub-retailer under ${node.name}`} onClick={() => setEdit({ id: null, creating: true, parentId: node.id })}><Plus size={13} /> Sub-retailer</button>
                  )}
                  <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={() => setEdit({ id: node.id, creating: false })}><Settings size={13} /> {isSuperAdmin ? 'Settings' : 'Details'}</button>
                </div>
              </div>

              <ul className="nav nav-tabs mb-3">
                <li className="nav-item"><button type="button" className={`nav-link d-inline-flex align-items-center gap-1 ${tab === 'locations' ? 'active' : ''}`} onClick={() => setTab('locations')}><MapPin size={14} /> Locations <span className="badge text-bg-light border">{counts?.locations ?? 0}</span></button></li>
                <li className="nav-item"><button type="button" className={`nav-link d-inline-flex align-items-center gap-1 ${tab === 'people' ? 'active' : ''}`} onClick={() => setTab('people')}><Users size={14} /> People <span className="badge text-bg-light border">{counts?.people ?? 0}</span>{(counts?.waiting ?? 0) > 0 && <span className="badge text-bg-warning">{counts?.waiting} waiting</span>}</button></li>
              </ul>

              {tab === 'locations'
                ? <LocationsPane key={node.id} node={node} nodes={nodes} canEdit={isSuperAdmin} onChanged={() => void load()} />
                : <PeoplePane key={node.id} node={node} nodes={nodes} canEdit={isSuperAdmin} onChanged={() => void load()} />}
            </div>
          ) : (
            <div className="bg-white rounded-3 shadow-sm p-4 text-muted small">{loading ? 'Loading…' : 'Pick a retailer on the left.'}</div>
          )}
        </div>
      </div>

      {edit && (
        <AdminRetailerEditModal
          retailerId={edit.id}
          isCreating={edit.creating}
          initialName={edit.name}
          initialParentId={edit.parentId}
          canEdit={isSuperAdmin}
          onClose={() => setEdit(null)}
          onSaved={() => void load()}
          onDeleted={async () => {
            const target = edit.id !== null ? nodes.find(n => n.id === edit.id) : undefined;
            setEdit(null);
            if (!target) return;
            try {
              await deleteAdminRetailer(target.id, target.name);
              navigate('/admin/retailers');
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Delete failed');
            }
            void load();
          }}
        />
      )}
    </div>
  );
};

export default RetailersWorkspace;
